// src/ui/EditPostModal.jsx — change a post after it went out (the phone's EditPostSheet).
//
// The words, who can see it, the people tagged in it and whether people may
// comment; the post says "Edited" afterwards. What gets written is
// services/postEdit (the phone's own rule); the tags go back through the server
// (tagRoutes.announce) exactly as when posting. A poll keeps its question —
// people have already voted on it.
import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { editPatch } from '../services/postEdit';
import { tagsForPost, safetyForPost } from '../services/videoTags';
import { announceTags } from '../services/vibeSocial';
import TagPicker from './TagPicker';
import { X, Tag } from './icons';

export default function EditPostModal({ post, myUid, friendIds, onClose, onSaved }) {
  const status = post.tagStatus || {};
  const names = post.tagNames || {};
  const [text, setText] = useState(String(post.content || ''));
  const [audience, setAudience] = useState(post.audience === 'friends' ? 'friends' : 'everyone');
  const [comments, setComments] = useState(post.commentsEnabled !== false);
  const [tags, setTags] = useState((Array.isArray(post.taggedUids) ? post.taggedUids : [])
    .filter((u) => status[u] !== 'removed' && status[u] !== 'declined').map((u) => ({ uid: u, name: String(names[u] || '') })));
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const isPoll = post.type === 'poll';

  const save = async () => {
    const isVideo = (post.media || []).some((m) => m && m.type === 'video') || !!post.videoUrl;
    const patch = editPatch(post, { text, audience, commentsEnabled: comments, tags, isVideo, isPoll }, { tagsForPost, safetyForPost });
    if (!patch) { onClose(); return; }
    setBusy(true); setErr('');
    try {
      await updateDoc(doc(db, 'posts', post.id), { ...patch.fields, editedAt: serverTimestamp() });
      if (patch.tagsChanged) announceTags(post.id).catch(() => {});
      onSaved && onSaved(post.id, { ...patch.fields, editedAt: new Date() });
      onClose();
    } catch { setErr('Your changes couldn’t be saved — try again.'); setBusy(false); }
  };

  return (
    <div className="vb-fixed-share" onClick={onClose}>
      <div className="vb-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Edit post">
        <div className="vb-sheet-head">
          <b>Edit post</b>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><X width={18} height={18} /></button>
        </div>
        <div className="vb-sheet-body form">
          {isPoll
            ? <div className="vb-sub">A poll’s question can’t be changed after people have voted on it.</div>
            : <textarea className="vb-edit-text" value={text} onChange={(e) => setText(e.target.value)} rows={6} maxLength={5000} autoFocus placeholder="What’s on your mind?" />}
          <div className="vb-seg" role="group" aria-label="Audience">
            <span className="vb-seg-label">Visible to</span>
            <button className={audience === 'everyone' ? 'on' : ''} onClick={() => setAudience('everyone')}>Everyone</button>
            <button className={audience === 'friends' ? 'on' : ''} onClick={() => setAudience('friends')}>Friends</button>
          </div>
          <div className="vb-tagchips" style={{ padding: 0 }}>
            <Tag width={13} height={13} />
            {tags.map((x) => (
              <button key={x.uid} className="vb-tagchip" onClick={() => setTags((cur) => cur.filter((y) => y.uid !== x.uid))} title="Remove">{x.name || '…'} <X width={11} height={11} /></button>
            ))}
            <button className="vb-tagchip" onClick={() => setPicking(true)}>{tags.length ? 'Change' : 'Tag people'}</button>
          </div>
          <label className="vb-edit-check"><input type="checkbox" checked={comments} onChange={(e) => setComments(e.target.checked)} /> Allow comments</label>
          {err && <div className="vb-sub" style={{ color: '#f04438' }}>{err}</div>}
        </div>
        <div className="vb-sheet-foot">
          <button className="lg-go" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
      {picking && <TagPicker value={tags} friendIds={friendIds} myUid={myUid} onDone={(l) => { setTags(l); setPicking(false); }} onClose={() => setPicking(false)} />}
    </div>
  );
}
